import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Timestamp,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface FormSubmission {
  id: string;
  templateId: string;           // Firebase field: templateId (INDEXED)
  templateName?: string;        // Firebase field: templateName
  organizationId: string;       // Firebase field: organizationId (INDEXED)
  submittedBy: string;          // Firebase field: submittedBy (INDEXED)
  submittedByName: string;      // Firebase field: submittedByName (INDEXED)
  status: 'draft' | 'submitted' | 'approved' | 'rejected'; // Firebase field: status (INDEXED)
  data: Record<string, any>;    // Firebase field: data (form field values - NOT INDEXED)
  createdAt: Date;             // Firebase field: createdAt (INDEXED)
  updatedAt: Date;             // Firebase field: updatedAt (INDEXED)
  metadata?: {                 // Firebase field: metadata (optional)
    source?: string;           // e.g., 'form_filler', 'api', 'import'
    userAgent?: string;        // Browser user agent
    ipAddress?: string;        // User IP address
    referrer?: string;         // Referrer URL
  };
}

export interface FormSubmissionQuery {
  formTemplateId?: string;      // Filter by templateId field (INDEXED) - optional for admin queries
  organizationId?: string;      // Filter by organizationId field (INDEXED)
  status?: string;              // Filter by status field (INDEXED)
  submittedBy?: string;         // Filter by submittedBy field (INDEXED)
  pageSize?: number;            // Number of items per page (default: 25)
  lastDoc?: DocumentSnapshot;   // Last document for pagination
  sortField?: string;           // Sort by field (only indexed fields supported for server-side sort)
  sortDirection?: 'asc' | 'desc'; // Sort direction (default: desc)
  searchTerm?: string;          // Search term for client-side filtering
  filters?: Record<string, any>; // Additional filters for client-side filtering
}

const FORM_SUBMISSIONS_COLLECTION = 'formSubmissions';

/**
 * Optimized FormSubmissionService that minimizes Firebase index requirements
 * 
 * REQUIRED FIREBASE INDEXES (create these manually):
 * 1. templateId (single field, ascending/descending)
 * 2. organizationId (single field, ascending/descending) 
 * 3. status (single field, ascending/descending)
 * 4. submittedBy (single field, ascending/descending)
 * 5. createdAt (single field, ascending/descending)
 * 6. updatedAt (single field, ascending/descending)
 * 
 * COMPOSITE INDEXES (create these manually):
 * 1. templateId + organizationId + createdAt
 * 2. templateId + status + createdAt
 * 3. templateId + submittedBy + createdAt
 * 
 * NO DYNAMIC FIELD INDEXES NEEDED - all form field filtering is done client-side
 */
export class OptimizedFormSubmissionService {
  // Helper function to safely convert Firestore timestamp to Date
  private static convertTimestamp(timestamp: any): Date {
    if (!timestamp) return new Date();
    
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    
    if (timestamp.seconds) {
      return new Timestamp(timestamp.seconds, timestamp.nanoseconds || 0).toDate();
    }
    
    return new Date(timestamp);
  }

  // Submit a new form
  static async submitForm(submission: Omit<FormSubmission, 'id' | 'createdAt' | 'updatedAt'>): Promise<FormSubmission> {
    try {
      const now = Timestamp.now();
      
      const submissionData = {
        ...submission,
        status: submission.status || 'submitted',
        createdAt: now,
        updatedAt: now
      };

      const docRef = await addDoc(collection(db, FORM_SUBMISSIONS_COLLECTION), submissionData);
      
      return {
        ...submissionData,
        id: docRef.id,
        createdAt: now.toDate(),
        updatedAt: now.toDate()
      } as FormSubmission;
    } catch (error) {
      console.error('Error submitting form:', error);
      throw new Error('Failed to submit form');
    }
  }

  // Get form submissions with optimized querying
  static async getFormSubmissions(queryParams: FormSubmissionQuery): Promise<{
    submissions: FormSubmission[];
    hasMore: boolean;
    lastDoc?: DocumentSnapshot;
  }> {
    try {
      const {
        formTemplateId,
        organizationId,
        status,
        submittedBy,
        pageSize = 25,
        lastDoc,
        sortField = 'createdAt',
        sortDirection = 'desc',
        searchTerm,
        filters
      } = queryParams;

      // Build base query - only use indexed fields for WHERE clauses
      let q = query(collection(db, FORM_SUBMISSIONS_COLLECTION));

      // Add templateId filter if provided
      if (formTemplateId) {
        q = query(q, where('templateId', '==', formTemplateId));
      }

      // Add indexed filters
      if (organizationId) {
        q = query(q, where('organizationId', '==', organizationId));
      }

      if (status) {
        q = query(q, where('status', '==', status));
      }

      if (submittedBy) {
        q = query(q, where('submittedBy', '==', submittedBy));
      }

      // Only sort by indexed fields to avoid composite index requirements
      const indexedSortFields = ['createdAt', 'updatedAt', 'status', 'submittedBy', 'submittedByName'];
      const finalSortField = indexedSortFields.includes(sortField) ? sortField : 'createdAt';
      
      q = query(q, orderBy(finalSortField, sortDirection));

      // Add pagination
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      // Determine fetch size based on filtering needs
      const needsClientFiltering = searchTerm || filters || (sortField && !indexedSortFields.includes(sortField));
      const fetchSize = needsClientFiltering ? Math.max(pageSize * 5, 200) : pageSize + 1;
      
      q = query(q, limit(fetchSize));

      const querySnapshot = await getDocs(q);
      let submissions: FormSubmission[] = [];
      
      // Convert documents to submissions
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        submissions.push({
          id: doc.id,
          templateId: data.templateId,
          templateName: data.templateName,
          organizationId: data.organizationId,
          submittedBy: data.submittedBy,
          submittedByName: data.submittedByName || data.submittedBy,
          status: data.status || 'draft',
          data: data.data || {},
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          metadata: data.metadata
        } as FormSubmission);
      });

      // Apply client-side filtering for dynamic form fields and search
      if (needsClientFiltering) {
        submissions = this.applyClientSideFilters(submissions, {
          searchTerm,
          filters,
          sortField: indexedSortFields.includes(sortField) ? undefined : sortField,
          sortDirection
        });
      }

      // Apply pagination after filtering
      const paginatedSubmissions = submissions.slice(0, pageSize);
      const hasMore = needsClientFiltering ? submissions.length > pageSize : querySnapshot.docs.length > pageSize;
      
      // Find the last document for pagination
      let lastDocument: DocumentSnapshot | undefined;
      if (paginatedSubmissions.length > 0) {
        const lastSubmissionId = paginatedSubmissions[paginatedSubmissions.length - 1].id;
        lastDocument = querySnapshot.docs.find(doc => doc.id === lastSubmissionId);
      }

      return { submissions: paginatedSubmissions, hasMore, lastDoc: lastDocument };
    } catch (error) {
      console.error('Error getting form submissions:', error);
      throw new Error('Failed to get form submissions');
    }
  }

  // Client-side filtering for dynamic fields (no Firebase indexes needed)
  private static applyClientSideFilters(
    submissions: FormSubmission[],
    options: {
      searchTerm?: string;
      filters?: Record<string, any>;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
    }
  ): FormSubmission[] {
    let filtered = [...submissions];

    // Apply search term across all data fields
    if (options.searchTerm) {
      const searchLower = options.searchTerm.toLowerCase();
      filtered = filtered.filter(submission => {
        // Search in metadata fields
        const searchableFields = [
          submission.submittedByName || '',
          submission.templateName || '',
          submission.status || '',
          submission.submittedBy || ''
        ];

        // Search in form data fields
        if (submission.data) {
          Object.values(submission.data).forEach(value => {
            if (value !== null && value !== undefined) {
              searchableFields.push(String(value));
            }
          });
        }

        const searchableText = searchableFields.join(' ').toLowerCase();
        return searchableText.includes(searchLower);
      });
    }

    // Apply dynamic field filters
    if (options.filters) {
      filtered = filtered.filter(submission => {
        return Object.entries(options.filters || {}).every(([fieldName, filterValue]) => {
          const fieldValue = submission.data?.[fieldName];
          
          if (filterValue === null || filterValue === undefined || filterValue === '') {
            return true;
          }
          
          // Handle different field types
          if (typeof filterValue === 'string') {
            return String(fieldValue || '').toLowerCase().includes(filterValue.toLowerCase());
          }
          
          if (typeof filterValue === 'boolean') {
            return Boolean(fieldValue) === filterValue;
          }
          
          if (typeof filterValue === 'number') {
            return Number(fieldValue) === filterValue;
          }
          
          return fieldValue === filterValue;
        });
      });
    }

    // Apply dynamic field sorting
    if (options.sortField && options.sortDirection) {
      filtered.sort((a, b) => {
        let aValue = a.data?.[options.sortField!];
        let bValue = b.data?.[options.sortField!];
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // Convert to comparable values based on type
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          // Numbers can be compared directly
        } else {
          // Convert everything else to string for comparison
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }
        
        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        if (aValue > bValue) comparison = 1;
        
        return options.sortDirection === 'desc' ? -comparison : comparison;
      });
    }

    return filtered;
  }

  // Get submission count for a form template
  static async getSubmissionCount(formTemplateId?: string, organizationId?: string): Promise<number> {
    try {
      let q = query(collection(db, FORM_SUBMISSIONS_COLLECTION));

      if (formTemplateId) {
        q = query(q, where('templateId', '==', formTemplateId));
      }

      if (organizationId) {
        q = query(q, where('organizationId', '==', organizationId));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting submission count:', error);
      return 0;
    }
  }

  // Alias methods for compatibility
  static async createSubmission(submission: Omit<FormSubmission, 'id' | 'createdAt' | 'updatedAt'>): Promise<FormSubmission> {
    return this.submitForm(submission);
  }

  static async getSubmissions(queryParams: FormSubmissionQuery) {
    return this.getFormSubmissions(queryParams);
  }

  static async getUserSubmissions(userId: string, queryParams: Partial<FormSubmissionQuery> = {}) {
    return this.getFormSubmissions({
      ...queryParams,
      submittedBy: userId
    });
  }

  static async approveSubmission(submissionId: string): Promise<void> {
    try {
      const docRef = doc(db, FORM_SUBMISSIONS_COLLECTION, submissionId);
      await updateDoc(docRef, {
        status: 'approved',
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error approving submission:', error);
      throw new Error('Failed to approve submission');
    }
  }

  static async rejectSubmission(submissionId: string): Promise<void> {
    try {
      const docRef = doc(db, FORM_SUBMISSIONS_COLLECTION, submissionId);
      await updateDoc(docRef, {
        status: 'rejected',
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error rejecting submission:', error);
      throw new Error('Failed to reject submission');
    }
  }
}

// Export for compatibility
export const FormSubmissionService = OptimizedFormSubmissionService;
export default OptimizedFormSubmissionService;
